<template>
  <div class="animated fadeIn">
    <b-card header="Search Filter">
      <b-form>
        <b-row>
          <b-col md="6" class="mb-3">
            <label for="keyword">Keyword</label>
            <b-input-group>
              <b-input-group-prepend><b-input-group-text><i class="icon-magnifier"/></b-input-group-text></b-input-group-prepend>
              <b-form-input id="keyword" v-model.trim="form.keyword" type="text" class="form-control" name="keyword" />
            </b-input-group>
          </b-col>
          <b-col md="6" class="mb-3">
            <label for="status">Status</label>
            <b-form-select id="status" v-model="form.status" :options="statusOptions" name="status" />
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6" class="mb-3">
            <label for="start-date">Start Date</label>
            <b-input-group>
              <b-input-group-prepend><b-input-group-text><i class="icon-calendar"/></b-input-group-text></b-input-group-prepend>
              <date-pick v-model="form.start_date" :display-format="'DD-MM-YYYY'" :input-attributes="{class:'form-control', id:'start-date', name:'start_date'}"/>
            </b-input-group>
          </b-col>
          <b-col md="6" class="mb-3">
            <label for="end-date">End Date</label>
            <b-input-group>
              <b-input-group-prepend><b-input-group-text><i class="icon-calendar"/></b-input-group-text></b-input-group-prepend>
              <date-pick v-model="form.end_date" :display-format="'DD-MM-YYYY'" :input-attributes="{class:'form-control', id:'end-date', name:'end_date'}"/>
            </b-input-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6" class="mb-3">
            <label for="sort-by">Sort By</label>
            <b-form-select id="sort-by" v-model="form.sort_by" :options="sortOptions" name="sort_by" />
          </b-col>
          <b-col md="6" class="mb-3">
            <label for="per_page">Item per Page</label>
            <b-form-select id="per-page" v-model="form.per_page" :options="perPageOptions" name="per_page" />
          </b-col>
        </b-row>
        <b-button :disabled="storeState.isChildLoading || isNotValidDateRange" variant="primary" @click="handleSearchOrders">Search</b-button>
        <b-button :disabled="storeState.isChildLoading" variant="success" class="ml-2" @click="handleDownloadOrders">Download</b-button>
      </b-form>
    </b-card>
    <b-card header="Orders List">
      <b-table v-if="items" :items="items" :fields="fields" bordered hover responsive class="text-nowrap">
        <template slot="gold_weight" slot-scope="data">
          {{ data.item.gold_weight }} gr
        </template>
        <template slot="partner_gold_price" slot-scope="data">
          {{ data.item.partner_gold_price | toRupiah }}
        </template>
        <template slot="partner_buy" slot-scope="data">
          {{ data.item.partner_buy | toRupiah }}
        </template>
        <template slot="partner_sell" slot-scope="data">
          {{ data.item.partner_sell | toRupiah }}
        </template>
        <template slot="partner_margin_percentage" slot-scope="data">
          {{ data.item.partner_margin_percentage }}%
        </template>
        <template slot="partner_incentive" slot-scope="data">
          {{ data.item.partner_incentive | toRupiah }}
        </template>
      </b-table>
      <p v-if="!items" class="mb-0">No data</p>
      <b-pagination v-if="items && totalRows > params.per_page" :disabled="storeState.isChildLoading" :total-rows="totalRows" v-model="params.page" :per-page="params.per_page" align="center" first-text="First" prev-text="Prev" next-text="Next" last-text="Last" size="md" class="mt-4" @change="updateQueryString" />
    </b-card>
  </div>
</template>

<script>
import DatePick from 'vue-date-pick'
import { store } from '@/shared/store'
import { toRupiah, handleDownloadFile } from '@/shared/utils'
import { getOrdersList, getOrdersListCsv, handleError } from '@/services/metal'

export default {
  name: 'OrdersList',
  components: { DatePick },

  filters: {
    toRupiah: function(val) {
      return toRupiah(val)
    },
  }, // filters

  data() {
    return {
      params: {},
      form: {},
      statusOptions: [
        { value: '', text: 'All' },
        { value: 'PAID', text: 'Paid' },
        { value: 'UNPAID', text: 'Unpaid' },
        { value: 'CANCEL', text: 'Cancel' },
      ],
      sortOptions: [
        { value: 'date_desc', text: 'Newest date' },
        { value: 'date_asc', text: 'Oldest date' },
      ],
      perPageOptions: [
        { value: 10, text: '10' },
        { value: 25, text: '25' },
        { value: 50, text: '50' },
        { value: 75, text: '75' },
        { value: 100, text: '100' }
      ],
      fields: [
        'created_at',
        'updated_at',
        'customer_name',
        'customer_phone',
        'customer_email',
         {
          key: 'external_customer_id',
          label: 'Customer ID'
        },
        'contract_number',
        'trx_ref_number',
        'gold_weight',
        {
          key: 'partner_gold_price',
          label: 'Gold Price'
        },
        {
          key: 'partner_buy',
          label: 'Partner Buy Price'
        },
        {
          key: 'partner_sell',
          label: 'Partner Sell Price'
        },
        {
          key: 'partner_margin_percentage',
          label: 'Margin'
        },
        {
          key: 'partner_incentive_type',
          label: 'Incentive Type'
        },
        {
          key: 'partner_incentive',
          label: 'Incentive'
        },
        'status',
      ],
      isNotValidDateRange: false,
      items: null,
      totalRows: 0,
      storeState: store.state,
    }
  }, // data

  watch: {
    '$route': function() {
      store.setIsChildLoading(true)
      this.getOrdersList()
    },
    'form.start_date': function() {
    this.validateDateRange()
    },
    'form.end_date': function() {
      this.validateDateRange()
    }
  }, // watch

  created() {
    store.setIsInitLoading(true)
    this.getOrdersList()
  }, // created

  methods: {
    getOrdersList() {
      store.setIsProcessing(true)

      // assign default value
      this.params = {
        page: 1,
        keyword: '',
        status: '',
        start_date: '',
        end_date: '',
        sort_by: 'date_desc',
        per_page: 10,
      }

      // overwrite value from query string
      if (Object.keys(this.$route.query).length > 0) {
        if (this.$route.query.page) {
          this.$route.query.page = parseInt(this.$route.query.page)
        }

        if (this.$route.query.per_page) {
          this.$route.query.per_page = parseInt(this.$route.query.per_page)
        }

        this.params = Object.assign(this.params, this.$route.query)
      }

      // overwrite form value
      this.form = Object.assign({}, this.params)

      let reqData = {
        search: this.params.keyword,
        status: this.params.status,
        start_date: this.params.start_date,
        end_date: this.params.end_date,
        sort_by: this.params.sort_by,
        offset: (this.params.page-1)*this.params.per_page,
        limit: this.params.per_page,
      }

      getOrdersList(reqData)
        .then( response => {
          if (response.data.data.total_data != 0) {
            this.items = response.data.data.list_of_orders
            this.totalRows = response.data.data.total_data
          } else {
            this.items = null
            this.totalRows = 0
          }
          store.setIsInitLoading(false)
        })
        .catch( error => {
          handleError(error)
        })
        .then( () => {
          store.clearProcessing()
        })
    },

    updateQueryString(page) {
      this.params.page = page
      this.$router.push({ path: '/orders-list', query: this.params})
    },

    handleSearchOrders() {
      // overwrite params value using form value
      this.params = Object.assign(this.params, this.form)
      this.updateQueryString(1)
    },

    validateDateRange() {
      if (this.form.start_date != '' && this.form.end_date != '') {
        if (this.$data.form.start_date > this.$data.form.end_date) {
          window.scrollTo(0,0)
          store.showAlert('Start Date is more than End Date', 'danger')
          this.isNotValidDateRange = true
        } else {
          store.hideAlert()
          this.isNotValidDateRange = false
        }
      } else {
        store.hideAlert()
        this.isNotValidDateRange = false
      }
    },

    handleDownloadOrders() {
      // overwrite params value using form value
      this.params = Object.assign(this.params, this.form)

      let reqData = {
        search: this.params.keyword,
        status: this.params.status,
        start_date: this.params.start_date,
        end_date: this.params.end_date,
        sort_by: this.params.sort_by,
      }

      getOrdersListCsv(reqData)
        .then( response => {
          handleDownloadFile(response)
          store.setIsInitLoading(false)
        })
        .catch( error => {
          handleError(error)
        })
        .then( () => {
          store.clearProcessing()
        })
    }
  }, // methods
}
</script>
